import { PageBuilderVideoPlayerPropertiesModel } from '@de-trustleap/shared-models';
import { BaseWidget } from './baseWidget';
import { Logger } from '../utils/logger';
import { hasVideoConsentAsync } from '../utils/consentManager';
import {
    getPrivacyPolicy,
    getImpressum,
    getInitialInfo,
    getTermsAndConditions
} from "../utils/getLegalDocument";

export class VideoPlayerWidget extends BaseWidget<PageBuilderVideoPlayerPropertiesModel> {
    private containerRef: HTMLElement | null = null;

    constructor(properties: PageBuilderVideoPlayerPropertiesModel) {
        super(properties);
    }

    async render(): Promise<HTMLElement> {
        const container = this.createContainer();
        this.containerRef = container;

        const { embedUrl, provider } = this.getEmbedUrl(this.properties.link);

        if (!embedUrl) {
            container.innerHTML = `<p style="color: red;">⚠️ Ungültiger Videolink</p>`;
            return container;
        }

        const hasConsent = await hasVideoConsentAsync();
        Logger.info("🎬 Videowiedergabe-Erlaubnis:", hasConsent);

        if (hasConsent) {
            const iframe = this.createVideoIframe(embedUrl, provider);
            container.appendChild(iframe);
        } else {
            const fallback = this.createClickToLoadFallback(embedUrl, provider);
            container.appendChild(fallback);
        }

        return container;
    }

    private createContainer(): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('video-player-widget');

        this.applyStyles(container, {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: `${this.properties.width ?? 300}px`,
            height: `${this.properties.height ?? 200}px`,
            overflow: 'hidden',
            position: 'relative',
            padding: '0',
            margin: '20px',
            backgroundColor: '#f8f8f8',
            border: '1px solid #ddd',
            borderRadius: '8px',
        });

        return container;
    }

    private createVideoIframe(embedUrl: string, provider: string | null): HTMLIFrameElement {
        const iframe = document.createElement('iframe');
        iframe.classList.add('video-player-frame');
        iframe.setAttribute('data-cmp-src', embedUrl);

        this.applyStyles(iframe, {
            width: '100%',
            height: '100%',
            border: '0',
        });

        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.sandbox = this.getSandboxRules(provider);

        iframe.src = embedUrl;
        return iframe;
    }

    private createClickToLoadFallback(embedUrl: string, provider: string | null): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.classList.add('video-click-to-load');

        this.applyStyles(wrapper, {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: `${this.properties.height ?? 200}px`,
            width: `${this.properties.width ?? 300}px`,
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '1em',
            textAlign: 'center',
            backgroundColor: '#000',
            fontFamily: 'sans-serif',
            fontSize: '14px',
            color: '#eee'
        });

        const message = document.createElement('p');
        message.innerHTML = `🎥 Dieses Video wird von ${provider?.toUpperCase() ?? 'einem externen Anbieter'} eingebettet. Beim Aktivieren können personenbezogene Daten an den Anbieter übermittelt werden. Es kann außerdem zu einer Datenübertragung in die USA kommen. Weitere Informationen finden Sie in der <a href="/legalpages/datenschutz.html" target="_blank" style="color: #cc0000; text-decoration: underline;">Datenschutzerklärung</a>.`;

        const label = document.createElement('p');
        label.textContent = 'Zum Laden des Videos bitte aktivieren:';
        label.style.marginBottom = '8px';

        const toggleWrapper = document.createElement('label');
        toggleWrapper.className = 'youtube-toggle';
        toggleWrapper.innerHTML = `
            <input type="checkbox">
            <span class="slider"></span>
        `;

        const checkbox = toggleWrapper.querySelector('input') as HTMLInputElement;

        checkbox.addEventListener('change', () => {
            if (checkbox.checked) {
                setTimeout(() => {
                    const autoPlayUrl = embedUrl.includes('?') ? `${embedUrl}&autoplay=1` : `${embedUrl}?autoplay=1`;
                    const iframe = this.createVideoIframe(autoPlayUrl, provider);
                    wrapper.replaceWith(iframe);
                }, 300);
            }
        });

        wrapper.appendChild(message);
        wrapper.appendChild(label);
        wrapper.appendChild(toggleWrapper);

        const existingStyle = document.getElementById('video-toggle-style');
        if (!existingStyle) {
            const style = document.createElement('style');
            style.id = 'video-toggle-style';
            style.innerHTML = `
                .youtube-toggle {
                    position: relative;
                    display: inline-block;
                    width: 50px;
                    height: 28px;
                }
                .youtube-toggle input {
                    display: none;
                }
                .youtube-toggle .slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: #555;
                    transition: .4s;
                    border-radius: 34px;
                }
                .youtube-toggle .slider:before {
                    position: absolute;
                    content: "";
                    height: 20px;
                    width: 20px;
                    left: 4px;
                    bottom: 4px;
                    background-color: white;
                    transition: .4s;
                    border-radius: 50%;
                }
                .youtube-toggle input:checked + .slider {
                    background-color: #cc0000;
                }
                .youtube-toggle input:checked + .slider:before {
                    transform: translateX(22px);
                }
            `;
            document.head.appendChild(style);
        }

        return wrapper;
    }

    private getEmbedUrl(videoUrl?: string): { embedUrl: string | null; provider: string | null } {
        if (!videoUrl) return { embedUrl: null, provider: null };

        const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/shorts\/)|youtu\.be\/)([\w-]{11})/;
        const vimeoRegex = /https?:\/\/(www\.)?vimeo\.com\/(\d+)/;

        if (youtubeRegex.test(videoUrl)) {
            return {
                embedUrl: `https://www.youtube-nocookie.com/embed/${videoUrl.match(youtubeRegex)?.[1]}?rel=0&showinfo=0`,
                provider: 'youtube',
            };
        }

        if (vimeoRegex.test(videoUrl)) {
            return {
                embedUrl: `https://player.vimeo.com/video/${videoUrl.match(vimeoRegex)?.[2]}`,
                provider: 'vimeo',
            };
        }

        return { embedUrl: null, provider: null };
    }

    private getSandboxRules(provider: string | null): string {
        switch (provider) {
            case 'youtube':
                return 'allow-scripts allow-same-origin allow-presentation';
            case 'vimeo':
                return 'allow-same-origin allow-presentation';
            default:
                return 'allow-presentation';
        }
    }
}